import { AxiosResponse } from 'axios';
import { SET_ANNOUNCEMENTS, SET_ANNOUNCEMENT, SET_ERRORMSG } from '@/store/mutation_types';
import {
  BULK_DELETE_ANNOUNCEMENTS,
  BULK_MOVE_ANNOUNCEMENTS_TO_FOLDER,
  CLEAR_ANNOUNCEMENT_ISLOADING,
  DELETE_ANNOUNCEMENT,
  EDIT_ANNOUNCEMENT,
  EXPORT_ANNOUNCEMENTS,
  GET_ANNOUNCEMENTS,
  MOVE_ANNOUNCEMENT_TO_FOLDER,
  REGISTER_ANNOUNCEMENT,
  TOGGLE_ANNOUNCEMENT_IS_ACTIVE,
} from '@/store/action_types';
import { StateObject } from '@/dtos/state.dto';
import Announcement from '@/services/announcement';
import { Module } from 'vuex';
import { EditAnnouncement, MoveAnnouncementToFolder, MoveAnnouncementsToFolder, RegisterAnnouncement, GetAnnouncement } from '@/dtos/announcement.dto';

const buildPaginationMeta = (metaData: any, payload: any) => {
  const perPage = Number(metaData?.perPage ?? payload?.limit ?? 20);
  const currentPage = Number(metaData?.currentPage ?? payload?.page ?? 1);
  const totalRecords = Number(metaData?.totalRecords ?? 0);
  const totalPages = Number(metaData?.totalPages ?? Math.max(Math.ceil(totalRecords / perPage), 1));

  return { currentPage, perPage, totalRecords, totalPages };
};

// Each list fetch takes a ticket and only the newest one may write the list,
// so a slower request that was superseded (an earlier sort, search, filter or
// page) can never land on top of the rows the user asked for last.
let latestAnnouncementsRequest = 0;

const announcement: Module<any, {}> = {
  state: {
    announcement: {
      data: null,
      isLoading: false,
    },
    announcements: {
      data: null,
      metaData: null,
      isLoading: false,
    },
  },
  getters: {
    announcement: (state: { announcement: any }) => state.announcement,
    announcements: (state: { announcements: any }) => state.announcements,
  },
  mutations: {
    [SET_ANNOUNCEMENT]: (
      state: { announcement: StateObject },
      payload: { data: null; status: any; isLoading: any }
    ) => {
      payload.data != null ? (state.announcement.data = payload.data) : null,
        (state.announcement.isLoading = payload.isLoading);
    },
    [SET_ANNOUNCEMENTS]: (
      state: { announcements: StateObject },
      payload: { data: null; metaData?: any; status: any; isLoading: any }
    ) => {
      payload.data != null ? (state.announcements.data = payload.data) : null,
        payload.metaData != null ? (state.announcements.metaData = payload.metaData) : null,
        (state.announcements.isLoading = payload.isLoading);
    },
  },
  actions: {
    [REGISTER_ANNOUNCEMENT]: async ({ commit }: any, payload: any) => {
      commit(SET_ANNOUNCEMENT, { data: null, isLoading: true });

      const result: AxiosResponse = await Announcement.registerAnnouncement(
        payload as RegisterAnnouncement
      );

      if (result) {
        if (result.data.status) {
          commit(SET_ANNOUNCEMENT, { data: result.data.data, isLoading: false });
          return true;
        }
        commit(SET_ANNOUNCEMENT, { data: null, isLoading: false });
        commit(SET_ERRORMSG, result.data.msg);
        return false;
      } else {
        commit(SET_ANNOUNCEMENT, { isLoading: false });
        return false;
      }
    },
    [GET_ANNOUNCEMENTS]: async ({ commit }: any, payload: any) => {
      const request = ++latestAnnouncementsRequest;
      commit(SET_ANNOUNCEMENTS, { data: null, isLoading: true });

      const result: AxiosResponse = await Announcement.getAnnouncements(
        payload as GetAnnouncement
      );

      if (request !== latestAnnouncementsRequest) return false;

      if (result) {
        if (result.data.status) {
          const records = Array.isArray(result.data.data) ? result.data.data : [];
          const metaData = buildPaginationMeta(result.data.metaData, payload);
          commit(SET_ANNOUNCEMENTS, { data: records, metaData, isLoading: false });
          return true;
        }
        commit(SET_ANNOUNCEMENTS, { data: null, isLoading: false });
        commit(SET_ERRORMSG, result.data.msg);
        return false;
      } else {
        commit(SET_ANNOUNCEMENTS, { isLoading: false });
        return false;
      }
    },
    // Fetches announcements for export WITHOUT mutating the paginated
    // `announcements` list that backs the visible table, so the table behind the
    // export modal stays intact. Returns the raw records array (or null on failure).
    [EXPORT_ANNOUNCEMENTS]: async (_ctx: any, payload: any) => {
      const result: AxiosResponse = await Announcement.getAnnouncements(
        payload as GetAnnouncement
      );

      if (result && result.data && result.data.status) {
        return Array.isArray(result.data.data) ? result.data.data : [];
      }

      return null;
    },
    [MOVE_ANNOUNCEMENT_TO_FOLDER]: async ({ commit }: any, payload: any) => {
      commit(SET_ANNOUNCEMENT, { data: { id: payload.id }, isLoading: true });

      let result: AxiosResponse;
      try {
        result = await Announcement.moveAnnouncementToFolder(
          payload as MoveAnnouncementToFolder
        );
      } catch (error) {
        commit(SET_ANNOUNCEMENT, { data: null, isLoading: false });
        throw error;
      }

      if (result) {
        if (result.data.status) {
          commit(SET_ANNOUNCEMENT, { data: result.data.data, isLoading: false });
          return true;
        }
        commit(SET_ANNOUNCEMENT, { data: null, isLoading: false });
        commit(SET_ERRORMSG, result.data.msg);
        return false;
      } else {
        commit(SET_ANNOUNCEMENT, { isLoading: false });
        return false;
      }
    },
    [BULK_MOVE_ANNOUNCEMENTS_TO_FOLDER]: async ({ commit }: any, payload: MoveAnnouncementsToFolder) => {
      commit(SET_ANNOUNCEMENT, { data: null, isLoading: true });

      let result: AxiosResponse;
      try {
        result = await Announcement.moveAnnouncementsToFolder(payload);
      } catch (error) {
        commit(SET_ANNOUNCEMENT, { data: null, isLoading: false });
        throw error;
      }

      if (result?.data?.status) {
        commit(SET_ANNOUNCEMENT, { data: result.data.data, isLoading: false });
        return true;
      }

      commit(SET_ANNOUNCEMENT, { data: null, isLoading: false });
      if (result?.data?.msg) commit(SET_ERRORMSG, result.data.msg);
      return false;
    },
    [TOGGLE_ANNOUNCEMENT_IS_ACTIVE]: async ({ commit, state }: any, payload: any) => {
      commit(SET_ANNOUNCEMENT, { data: { id: payload.id }, isLoading: true });

      const result: AxiosResponse = await Announcement.toggleAnnouncementIsActive(
        payload as EditAnnouncement
      );

      if (result) {
        if (result.data.status) {
          let index;
          if (state.announcements.data) {
            state.announcements.data.forEach((e: any, i: number) => {
              if (e.id == result.data.data.id) {
                index = i;
              }
            });
            if (index != null)
              state.announcements.data[index].isActive = result.data.data.isActive;
          }
          commit(SET_ANNOUNCEMENT, { data: result.data.data, isLoading: false });
          return true;
        }
        commit(SET_ANNOUNCEMENT, { data: null, isLoading: false });
        commit(SET_ERRORMSG, result.data.msg);
        return false;
      } else {
        commit(SET_ANNOUNCEMENT, { isLoading: false });
        return false;
      }
    },
    [EDIT_ANNOUNCEMENT]: async ({ commit }: any, payload: any) => {
      commit(SET_ANNOUNCEMENT, { data: null, isLoading: true });

      const result: AxiosResponse = await Announcement.editAnnouncement(
        payload as EditAnnouncement
      );

      if (result) {
        if (result.data.status) {
          console.log('result.data.data', result.data.data);
          commit(SET_ANNOUNCEMENT, { data: result.data.data, isLoading: false });
          return true;
        }
        commit(SET_ANNOUNCEMENT, { data: null, isLoading: false });
        commit(SET_ERRORMSG, result.data.msg);
        return false;
      } else {
        commit(SET_ANNOUNCEMENT, { isLoading: false });
        return false;
      }
    },
    [DELETE_ANNOUNCEMENT]: async ({ commit }: any, payload: string) => {
      commit(SET_ANNOUNCEMENT, { data: null, isLoading: true });

      let result: AxiosResponse;
      try {
        result = await Announcement.deleteAnnouncement(payload);
      } catch (error) {
        commit(SET_ANNOUNCEMENT, { data: null, isLoading: false });
        throw error;
      }

      if (result) {
        if (result.data.status) {
          console.log('result.data.data', result.data.data);
          commit(SET_ANNOUNCEMENT, { data: result.data.data, isLoading: false });
          return true;
        }
        commit(SET_ANNOUNCEMENT, { data: null, isLoading: false });
        commit(SET_ERRORMSG, result.data.msg);
        return false;
      } else {
        commit(SET_ANNOUNCEMENT, { isLoading: false });
        return false;
      }
    },
    [BULK_DELETE_ANNOUNCEMENTS]: async ({ commit }: any, ids: string[]) => {
      commit(SET_ANNOUNCEMENT, { data: null, isLoading: true });

      let result: AxiosResponse;
      try {
        result = await Announcement.deleteAnnouncements(ids);
      } catch (error) {
        commit(SET_ANNOUNCEMENT, { data: null, isLoading: false });
        throw error;
      }

      if (result?.data?.status) {
        commit(SET_ANNOUNCEMENT, { data: result.data.data, isLoading: false });
        return true;
      }

      commit(SET_ANNOUNCEMENT, { data: null, isLoading: false });
      if (result?.data?.msg) commit(SET_ERRORMSG, result.data.msg);
      return false;
    },
    [CLEAR_ANNOUNCEMENT_ISLOADING]: ({ commit }) => {
      commit(SET_ANNOUNCEMENT, { isLoading: false });
      commit(SET_ANNOUNCEMENTS, { isLoading: false });
    },
  },
};

export default announcement;
